import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAppSelector } from "@redux/hooks";
import { RootState } from "@redux/store/store";
import { ReactComponent as IconDocs } from "@assets/Icons/IconReport.svg";
import { apiAreaItem } from "../../SideMenu/MenuData";
import { filterAccountAreaItems } from "../../SideMenu/sideMenuVisibility";
import { isProductionEnvironment } from "../../SideMenu/sideMenuEnv";
import {
  FOCUS_RING,
  ROW_GAP,
  ROW_ICON,
  ROW_PADDING,
  ROW_RADIUS,
  ROW_TEXT,
  TRANSITION_COLORS,
} from "../../SideMenu/sideMenuDensity";
import HeaderMenu from "./HeaderMenu";

/**
 * Where "API Docs" goes, derived from where the Customer API itself answers.
 *
 * The API host serves its own documentation at `/docs`, so there is no second
 * variable to configure and no way for the two to disagree about which
 * environment they describe: staging's dashboard reaches staging's docs because
 * it already reaches staging's API. A literal here would have shipped whichever
 * host was current the day it was written to every environment after it.
 *
 * **The path is the trap.** `REACT_APP_CUSTOMER_API_URL` is the API *base* and
 * carries `/api/customer/v1` — where endpoints are mounted, not where the docs
 * live. Appending to it yields `…/api/customer/v1/docs`, which 404s. Only the
 * origin survives.
 *
 * This is not the rail's Documentation button (`REACT_APP_GITBOOK_URL`): that
 * is GitBook, a different site with different content. The two stay separate.
 *
 * Returns `null` rather than a guess when the base is missing or unparseable.
 * An empty deployment variable would otherwise throw out of `new URL` and take
 * the whole header down on every route, and a fabricated host would send
 * customers somewhere that was never going to answer.
 */
export function customerApiDocsUrl(
  apiBaseUrl: string | undefined
): string | null {
  if (!apiBaseUrl) return null;

  try {
    return `${new URL(apiBaseUrl).origin}/docs`;
  } catch {
    return null;
  }
}

/**
 * Resolved once at module scope: the read is inlined at build time anyway, so
 * `source.define` has already substituted the literal and there is nothing to
 * re-evaluate per render.
 *
 * An unconfigured build inherits the unresolvable `.invalid` host that
 * `rsbuild.config.ts` defaults to, and that is deliberate there: a missing
 * variable must not quietly point customers at staging.
 */
export const API_DOCS_URL = customerApiDocsUrl(
  process.env.REACT_APP_CUSTOMER_API_URL
);

/**
 * The header's API control: a labelled button, beside the avatar, that opens
 * the two API destinations.
 *
 * API is the one account destination people come looking for by name, and every
 * arrangement that filed it under something else — the settings drill-in, then
 * the header's gear menu, then the tail of a settings group — buried it. Here
 * it wears its own name in the header, so reaching it costs one click and
 * finding it costs none.
 *
 * Who sees it is not decided here: `filterAccountAreaItems` answers for this
 * control exactly as it does for the rail's rows, which is what keeps it and
 * the `/account/api` route (gated by the same `isCustomerApiEnabled`, CTX-37)
 * from disagreeing about who may open it. Renders nothing when the row is
 * filtered out — including the docs row, which is about a product this tenant
 * cannot reach.
 */
export default function HeaderApiControl() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const tenantType = useAppSelector(
    (state: RootState) => state.userProfile.TenantType
  );

  const isVisible = useMemo(
    () =>
      filterAccountAreaItems(
        [apiAreaItem],
        tenantType,
        isProductionEnvironment()
      ).length > 0,
    [tenantType]
  );

  if (!isVisible || !apiAreaItem.path) return null;

  const { label, path, Icon } = apiAreaItem;
  const isActive = pathname.startsWith(path);

  const rowClass = `flex w-full items-center ${ROW_GAP} ${ROW_RADIUS} ${ROW_PADDING} text-left ${ROW_TEXT} ${TRANSITION_COLORS} ${FOCUS_RING} text-semantic-text-secondary hover:bg-semantic-state-active hover:text-semantic-text-brand`;

  return (
    <HeaderMenu
      label="API menu"
      panelWidthClass="w-44"
      triggerClassName={`${ROW_GAP} ${ROW_RADIUS} px-2 py-1 ${ROW_TEXT} ${
        isActive
          ? "font-semibold text-semantic-text-brand"
          : "text-semantic-text-secondary"
      }`}
      trigger={
        <>
          {Icon && <Icon className={`${ROW_ICON} shrink-0`} aria-hidden />}
          <span>{label}</span>
        </>
      }
    >
      {(dismiss) => (
        <div className="px-2 py-2" role="menu" aria-label="API">
          <button
            type="button"
            role="menuitem"
            className={rowClass}
            onClick={() => {
              navigate(path);
              dismiss();
            }}
          >
            {Icon && <Icon className={`${ROW_ICON} shrink-0`} aria-hidden />}
            <span>{label}</span>
          </button>

          {/* Absent when the Customer API base is unset or unparseable: a row
              that cannot say where it goes is worse than no row. The API row
              above still works — it navigates inside the app and depends on
              none of this. */}
          {API_DOCS_URL && (
            /* Leaves the app, so it is an anchor rather than a button: the
               browser's own affordances — open in a new tab, copy the link —
               are the point of a documentation row, and `onClick` would take
               them away. `noopener` because `_blank` hands the opened page a
               handle on this one otherwise. */
            <a
              href={API_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              role="menuitem"
              className={rowClass}
              onClick={dismiss}
            >
              {/* A document rather than the key: the key is the credential this
                  menu's other row manages, and wearing it twice would say the two
                  rows lead to the same thing. */}
              <IconDocs className={`${ROW_ICON} shrink-0`} aria-hidden />
              <span>API Docs</span>
            </a>
          )}
        </div>
      )}
    </HeaderMenu>
  );
}
